import { useState } from "react";
import { useForm } from "react-hook-form";

import FilterCollapse from "components/filter_form/filter_collapse/FilterCollapse";
import FilterCollapseHeader from "components/filter_form/filter_collapse_header/FilterCollapseHeader";
import FilterFormTitle from "components/filter_form/filter_form_title/FilterFormTitle";
import { countFilters } from "components/filter_form/utils";
import { camelToSentence } from "lib/utils";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import { bidFilterContents } from "./filter_groups/utils";
import { BidFilters, EMPTY_BID_FILTERS } from "./utils";

export const groupedKeys: Record<FilterGroupKeys, (keyof BidFilters)[]> = {
  assignedTo: ["assignee"],
  team: ["teamIds"],
  stage: ["stageIds"],
  createdDate: ["createdDate"],
  submissionDate: ["submissionDate"],
  awardDate: ["awardDate"],
  value: ["value"],
  lastUpdated: ["lastUpdatedDate"],
  buyers: ["buyers", "customBuyers", "buyerLists", "buyerCategories", "buyerCountryRegions"],
  suppliers: ["supplierGuids", "supplierSme"],
  createdBy: ["createdBy"],
};

type FilterGroupKeys = keyof typeof bidFilterContents;

type FilterFormProps = {
  onChange: (filters: BidFilters) => void;
  filters: BidFilters;
  hiddenFilters?: FilterGroupKeys[];
  onClose: () => void;
};

export function FilterForm({ onChange, filters, onClose, hiddenFilters }: FilterFormProps) {
  const { control, watch, trigger, getValues, reset, setValue } = useForm<BidFilters>({
    mode: "onChange",
    defaultValues: filters,
  });
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  const data = watch();

  useFormAutoSubmit({
    watch,
    trigger,
    getValues,
    onChange,
  });

  function clearAll() {
    reset({ ...EMPTY_BID_FILTERS, searchText: filters.searchText });
  }

  const counts = countFilters(filters, groupedKeys);

  function clearFilter(key: FilterGroupKeys) {
    const keys = groupedKeys[key];
    keys.forEach((k) => {
      setValue(k, EMPTY_BID_FILTERS[k]);
    });
  }

  const filterProps = {
    control,
    data,
  };

  function getFilterGroups() {
    let items = (Object.keys(groupedKeys) as FilterGroupKeys[]).map((key) => {
      const ContentComponent = bidFilterContents[key];

      return {
        key,
        header: (
          <FilterCollapseHeader
            title={camelToSentence(key)}
            count={counts[key]}
            clear={() => clearFilter(key)}
          />
        ),
        content: <ContentComponent {...filterProps} />,
      };
    });

    if (hiddenFilters?.length) {
      items = items.filter((item) => !hiddenFilters.includes(item.key));
    }

    return items;
  }

  const items = getFilterGroups();

  return (
    <form aria-label="Bid filters">
      <FilterFormTitle
        clearFilters={clearAll}
        showClear={Object.values(counts).some(Boolean)}
        onClose={onClose}
      />
      <FilterCollapse items={items} value={activeGroups} onChange={setActiveGroups} />
    </form>
  );
}
